import {
  ArrowRight,
  Check,
  FileText,
  HardDrive,
  Search,
  Star,
  Trash2,
} from 'lucide-react'

/**
 * Interface sketches for the vision page.
 *
 * <p>These depict NevTan AI as a finished platform, so most of what they show
 * does not exist yet. They live apart from the mockups used by the current
 * landing page, which are held to what actually ships — mixing the two would
 * make it impossible to keep that page honest.
 */

const CHROME = 'rounded-xl border border-brand-line bg-brand-paper shadow-raised overflow-hidden'

function TitleBar({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1.5 border-b border-brand-line bg-brand-panel px-3.5 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#27c93f]" />
      <span className="ml-2 text-[11px] font-medium text-brand-muted">{label}</span>
    </div>
  )
}

/* ------------------------------------------------------------- model hub */

const HUB_CATEGORIES = ['All', 'Chat', 'Reasoning', 'Coding', 'Vision', 'Image']

const HUB_MODELS = [
  {
    name: 'Qwen 3',
    family: 'General AI',
    stars: 4,
    ram: '16 GB',
    vram: '8 GB',
    size: '8 GB',
    fits: true,
  },
  {
    name: 'DeepSeek R1',
    family: 'Reasoning',
    stars: 5,
    ram: '32 GB',
    vram: '12 GB',
    size: '14 GB',
    fits: false,
  },
  {
    name: 'Qwen Coder',
    family: 'Coding',
    stars: 5,
    ram: '16 GB',
    vram: '6 GB',
    size: '6 GB',
    fits: true,
  },
]

export function ModelHubMockup() {
  return (
    <div className={CHROME}>
      <TitleBar label="Model hub" />

      <div className="border-b border-brand-line bg-brand-panel px-4 py-3">
        <div className="flex items-center gap-2 rounded-lg border border-brand-line bg-brand-paper px-3 py-2">
          <Search size={14} className="shrink-0 text-brand-muted" />
          <span className="text-[12.5px] text-brand-muted">Search models…</span>
        </div>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {HUB_CATEGORIES.map((category, i) => (
            <span
              key={category}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                i === 0
                  ? 'bg-brand text-white'
                  : 'border border-brand-line bg-brand-paper text-brand-body'
              }`}
            >
              {category}
            </span>
          ))}
        </div>
      </div>

      <ul className="divide-y divide-brand-line">
        {HUB_MODELS.map((model) => (
          <li key={model.name} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13.5px] font-semibold text-brand-heading">{model.name}</span>
                <span className="rounded-full bg-brand-tint px-2 py-0.5 text-[10px] font-semibold text-brand-tint-ink">
                  {model.family}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-0.5">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    size={11}
                    className={i < model.stars ? 'fill-brand text-brand' : 'text-brand-line-strong'}
                  />
                ))}
              </div>
            </div>

            <dl className="flex shrink-0 gap-4 text-[11px] text-brand-muted">
              {[
                ['RAM', model.ram],
                ['VRAM', model.vram],
                ['Size', model.size],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-[9.5px] uppercase tracking-wider">{label}</dt>
                  <dd className="font-medium text-brand-body">{value}</dd>
                </div>
              ))}
            </dl>

            {/* The hub knows the machine it is running on, so the button is
                already an answer rather than an invitation to find out. */}
            <span
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-[11.5px] font-semibold ${
                model.fits
                  ? 'bg-brand text-white'
                  : 'border border-brand-line text-brand-muted'
              }`}
            >
              {model.fits ? 'Install' : 'Too large'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ----------------------------------------------------- hardware recommender */

const RECOMMENDATIONS = [
  { tone: 'good', label: 'Qwen 3 8B', note: 'Comfortable — recommended' },
  { tone: 'good', label: 'Llama 8B', note: 'Comfortable' },
  { tone: 'warn', label: 'Mixtral 22B', note: 'Runs, but slowly' },
  { tone: 'bad', label: '70B models', note: 'Not enough memory' },
]

export function RecommenderMockup() {
  return (
    <div className={CHROME}>
      <TitleBar label="Recommended for this computer" />

      <div className="grid gap-px bg-brand-line sm:grid-cols-4">
        {[
          ['RAM', '16 GB'],
          ['GPU', 'RTX 4060'],
          ['VRAM', '8 GB'],
          ['CPU', 'Ryzen 7'],
        ].map(([label, value]) => (
          <div key={label} className="bg-brand-paper px-3.5 py-2.5">
            <p className="text-[9.5px] font-semibold uppercase tracking-wider text-brand-muted">
              {label}
            </p>
            <p className="mt-0.5 text-[13px] font-semibold text-brand-heading">{value}</p>
          </div>
        ))}
      </div>

      <ul className="divide-y divide-brand-line">
        {RECOMMENDATIONS.map((item) => (
          <li key={item.label} className="flex items-center gap-2.5 px-4 py-2.5">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                item.tone === 'good'
                  ? 'bg-[#15683f]'
                  : item.tone === 'warn'
                    ? 'bg-[#b1780f]'
                    : 'bg-[#b0202a]'
              }`}
            />
            <span className="text-[12.5px] font-medium text-brand-heading">{item.label}</span>
            <span className="ml-auto text-[11px] text-brand-muted">{item.note}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ------------------------------------------------------------- my models */

export function MyModelsMockup() {
  return (
    <div className={CHROME}>
      <TitleBar label="My models" />
      <ul className="divide-y divide-brand-line">
        {[
          { id: 'qwen3-8b', size: '8.0 GB', state: 'Default' },
          { id: 'qwen-coder-7b', size: '6.0 GB', state: 'Running' },
          { id: 'whisper-turbo', size: '1.5 GB', state: 'Update available' },
        ].map((model) => (
          <li key={model.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3">
            <code className="break-all text-[12px] font-medium text-brand-heading">{model.id}</code>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                model.state === 'Running'
                  ? 'bg-brand text-white'
                  : model.state === 'Default'
                    ? 'bg-brand-tint text-brand-tint-ink'
                    : 'border border-brand-line text-brand-muted'
              }`}
            >
              {model.state}
            </span>
            <span className="ml-auto shrink-0 text-[11px] text-brand-muted">{model.size}</span>
            <Trash2 size={13} className="shrink-0 text-brand-muted" />
          </li>
        ))}
      </ul>
      <p className="border-t border-brand-line bg-brand-panel px-4 py-2.5 text-[11px] text-brand-muted">
        15.5 GB used · set a default, switch, update or remove
      </p>
    </div>
  )
}

/* ---------------------------------------------------------- rag pipeline */

const PIPELINE = [
  { label: 'Your documents', note: 'PDFs, notes, code' },
  { label: 'Indexed locally', note: 'Chunked and embedded' },
  { label: 'Your question', note: 'Asked in plain language' },
  { label: 'Answer with sources', note: 'From a local model' },
]

export function RagPipelineMockup() {
  return (
    <div className={CHROME}>
      <TitleBar label="Ask your own documents" />
      <div className="p-4">
        <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {PIPELINE.map((step, i) => (
            <li
              key={step.label}
              className="relative rounded-lg border border-brand-line bg-brand-panel px-3 py-2.5"
            >
              <p className="text-[9.5px] font-bold tracking-widest text-brand-muted">
                {String(i + 1).padStart(2, '0')}
              </p>
              <p className="mt-0.5 text-[12.5px] font-semibold text-brand-heading">{step.label}</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-brand-muted">{step.note}</p>
              {i < PIPELINE.length - 1 && (
                <ArrowRight
                  size={13}
                  aria-hidden
                  className="absolute -right-[11px] top-1/2 hidden -translate-y-1/2 text-brand/50 lg:block"
                />
              )}
            </li>
          ))}
        </ol>

        <div className="mt-3 rounded-lg border border-brand-line bg-brand-paper p-3">
          <p className="text-[12.5px] text-brand-body">
            &ldquo;What did we decide about the retention policy?&rdquo;
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {['handbook.pdf · p.14', 'meeting-notes.md', 'policy-v3.docx'].map((source) => (
              <span
                key={source}
                className="inline-flex items-center gap-1 rounded-full bg-brand-tint px-2 py-0.5 text-[10px] font-medium text-brand-tint-ink"
              >
                <FileText size={9} /> {source}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- install flow */

const INSTALL_STEPS = [
  { label: 'Choose', note: 'From the hub' },
  { label: 'Check', note: 'Against your machine' },
  { label: 'Download', note: 'Resumable' },
  { label: 'Ready', note: 'Nothing to configure' },
]

export function InstallFlowMockup() {
  return (
    <div className={CHROME}>
      <TitleBar label="Installing Qwen 3 8B" />
      <div className="p-4">
        <div className="flex items-center gap-2">
          {INSTALL_STEPS.map((step, i) => (
            <div key={step.label} className="flex flex-1 items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${
                      i < 3 ? 'bg-brand text-white' : 'border border-brand-line text-brand-muted'
                    }`}
                  >
                    {i < 3 ? <Check size={10} /> : i + 1}
                  </span>
                  <span className="truncate text-[11.5px] font-semibold text-brand-heading">
                    {step.label}
                  </span>
                </div>
                <p className="mt-0.5 truncate pl-6 text-[10px] text-brand-muted">{step.note}</p>
              </div>
              {i < INSTALL_STEPS.length - 1 && (
                <span aria-hidden className="h-px w-4 shrink-0 bg-brand-line" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-3.5 h-1.5 overflow-hidden rounded-full bg-brand-panel">
          <div className="h-full w-[78%] rounded-full bg-brand" />
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-brand-muted">
          <HardDrive size={11} /> 6.2 GB of 8.0 GB · about 2 minutes left
        </p>
      </div>
    </div>
  )
}
